import { VerdictChip } from './Verdict';
import React from 'react';
import { Star, TrendingUp, TrendingDown, Pause } from 'lucide-react';
import LivePrice from './LivePrice';
import { recLabel } from '../../lib/recLabel';

const fmtPrice = (v) => {
    if (v === null || v === undefined) return '—';
    if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
    return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
};
const fmtVol = (v) => {
    if (!v) return '—';
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return `${v.toFixed(0)}`;
};

const recCfg = {
    LONG: { Icon: TrendingUp, wrap: 'border-emerald-400/40 bg-emerald-400/10', text: 'text-emerald-300' },
    SHORT: { Icon: TrendingDown, wrap: 'border-rose-500/40 bg-rose-500/10', text: 'text-rose-300' },
    WAIT: { Icon: Pause, wrap: 'border-slate-600 bg-slate-700/60', text: 'text-slate-300' },
};

export default function MobileCoinCard({ row, active, onSelect, onToggleFavorite, tick, isOpenPosition }) {
    const r = row;
    const chg = tick?.change_pct_24h ?? r.change_pct_24h ?? 0;
    const up = chg >= 0;
    const rec = r.recommendation || { action: 'WAIT' };
    const lbl = recLabel(rec);
    const c = lbl.weak ? recCfg.WAIT : recCfg[rec.action] || recCfg.WAIT;
    const RecIcon = c.Icon;

    return (
        <button
            onClick={() => onSelect?.(r.symbol)}
            data-testid={`mobile-card-${r.symbol}`}
            className={`block w-full text-left border rounded-md p-3 transition-colors ${
                active ? 'border-slate-500 bg-slate-700' : 'border-slate-700 bg-slate-800 hover:bg-slate-700/50 active:bg-slate-700'
            } ${r.is_favorite ? 'border-l-2 border-l-amber-400' : ''}`}
        >
            {/* row 1: symbol + price + change + star + multi-tf badge */}
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavorite?.(r.symbol, !r.is_favorite);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                e.preventDefault();
                                onToggleFavorite?.(r.symbol, !r.is_favorite);
                            }
                        }}
                        data-testid={`mobile-star-${r.symbol}`}
                        title={r.is_favorite ? 'Favorilerden çıkar' : 'Favorilere ekle'}
                        className={`shrink-0 h-7 w-7 rounded-sm flex items-center justify-center -ml-1 cursor-pointer ${
                            r.is_favorite ? 'text-amber-400' : 'text-slate-400'
                        }`}
                    >
                        <Star className={`h-4 w-4 ${r.is_favorite ? 'fill-current' : ''}`} />
                    </span>
                    <span className="text-slate-50 text-base font-medium tracking-tight truncate">
                        {r.symbol.replace('USDT', '')}
                    </span>
                    <span className="text-slate-400 text-[10px] uppercase shrink-0">USDT</span>
                    {r.multi_tf && (
                        <span
                            data-testid={`mobile-multi-tf-${r.symbol}`}
                            className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border shrink-0 ${
                                r.multi_tf.tier === 'STRONG'
                                    ? 'border-rose-400/60 bg-rose-500/15 text-rose-100 animate-pulse'
                                    : 'border-amber-400/40 bg-amber-500/10 text-amber-200'
                            }`}
                        >
                            {r.multi_tf.tier === 'STRONG' ? 'GÜÇLÜ' : 'ORTA'}
                        </span>
                    )}
                    {isOpenPosition && (
                        <span
                            data-testid={`mobile-open-pos-${r.symbol}`}
                            title="Açık pozisyonun var"
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/15 text-emerald-200 shrink-0"
                        >
                            💼
                        </span>
                    )}
                    {r.news_events && r.news_events.length > 0 && (
                        <span
                            data-testid={`mobile-news-${r.symbol}`}
                            title={r.news_events[r.news_events.length-1].title}
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/15 text-sky-200 shrink-0"
                        >
                            📰
                        </span>
                    )}
                </div>
                <div className="text-right shrink-0">
                    <div className="text-sm font-mono text-slate-100 leading-none">
                        <LivePrice symbol={r.symbol} tick={tick} fallbackPrice={r.price} className="text-sm" />
                    </div>
                    <div className={`text-[11px] font-mono leading-none mt-0.5 ${up ? 'text-emerald-400' : 'text-rose-500'}`}>
                        {up ? '+' : ''}{chg?.toFixed(2)}%
                    </div>
                </div>
            </div>

            {r.verdict && (
                <div className="flex items-center gap-2 mb-2" data-testid={`mobile-verdict-${r.symbol}`}>
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Karar</span>
                    <VerdictChip v={r.verdict} withDir />
                </div>
            )}
            {/* row 2: recommendation pill (full width) */}
            <div className={`flex items-center justify-between border rounded-sm px-2 py-1.5 ${c.wrap} mb-2`}>
                <div className={`flex items-center gap-1.5 ${c.text}`}>
                    <RecIcon className="h-3.5 w-3.5" />
                    <span className="text-[11px] font-bold uppercase tracking-wider" data-testid={`mobile-rec-label-${r.symbol}`}>{lbl.label}{lbl.official ? ' ✓ RESMİ' : lbl.weak ? ' · ZAYIF' : ''}</span>
                </div>
                {rec.action !== 'WAIT' && (
                    <div className="flex items-center gap-3 text-[11px] font-mono">
                        <span className={c.text}>+{rec.profit_pct}%</span>
                        <span className={`${rec.rr_ratio >= 1.5 ? c.text : 'text-amber-400'}`}>R/R {rec.rr_ratio}</span>
                    </div>
                )}
            </div>

            {/* row 3: TPs (only if not WAIT) */}
            {rec.action !== 'WAIT' && (
                <div className="grid grid-cols-3 gap-2 mb-2 text-[10px]">
                    <div>
                        <div className="text-slate-400 uppercase tracking-wider">Giriş</div>
                        <div className="text-slate-100 font-mono">${fmtPrice(rec.entry)}</div>
                    </div>
                    <div>
                        <div className={`uppercase tracking-wider ${c.text}`}>TP1</div>
                        <div className={`${c.text} font-mono`}>${fmtPrice(rec.tp1)}</div>
                    </div>
                    <div>
                        <div className={`uppercase tracking-wider ${c.text}`}>TP2</div>
                        <div className={`${c.text} font-mono`}>${fmtPrice(rec.tp2)}</div>
                    </div>
                </div>
            )}

            {/* row 4: scores + volume */}
            <div className="flex items-center justify-between text-[10px] text-slate-400 uppercase tracking-wider gap-3 pt-2 border-t border-slate-700/50">
                <div className="flex items-center gap-1">
                    <span>Fırsat</span>
                    <div className="h-1 w-10 bg-slate-600 rounded-sm overflow-hidden">
                        <div className="h-full bg-emerald-400" style={{ width: `${r.opportunity_score || 0}%` }} />
                    </div>
                    <span className="font-mono text-slate-300">{r.opportunity_score?.toFixed(0)}</span>
                </div>
                <div className="flex items-center gap-1">
                    <span>Risk</span>
                    <div className="h-1 w-10 bg-slate-600 rounded-sm overflow-hidden">
                        <div className={`h-full ${r.risk_score > 60 ? 'bg-rose-500' : r.risk_score > 30 ? 'bg-amber-400' : 'bg-slate-400'}`} style={{ width: `${r.risk_score || 0}%` }} />
                    </div>
                    <span className="font-mono text-slate-300">{r.risk_score?.toFixed(0)}</span>
                </div>
                <span className="font-mono text-slate-400">${fmtVol(r.volume_24h)}</span>
            </div>
        </button>
    );
}
